/**
 * Public Landing embed — crypto Workbench demo (WazirX TRX).
 * Client-only Cytoscape from demoGraphs; does not mount GraphCanvas or workbench stores.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CytoscapeComponent from 'react-cytoscapejs';
import type cytoscape from 'cytoscape';
import { Maximize2, Minus, Plus, Network, ExternalLink } from 'lucide-react';
import { demoGraphs } from '../data/demoGraphs';
import { workbenchHrefForDemo } from './dashboard/demoCatalog';
import { CanvasGridBackground } from './workbench/CanvasGridBackground';
import { enterGuestDemo } from '../utils/guestDemo';
import { hasActiveSession } from '../utils/session';

const DEMO_ID = 'wazirx-tron';

function landingWorkbenchStylesheet(isDark: boolean): any[] {
  const border = isDark ? '#475569' : '#cbd5e1';
  const bg = isDark ? '#1e293b' : '#f8fafc';
  const label = isDark ? '#e2e8f0' : '#0f172a';
  const edge = isDark ? '#64748b' : '#94a3b8';
  return [
    {
      selector: 'node',
      style: {
        shape: 'round-rectangle',
        width: 120,
        height: 40,
        'background-color': bg,
        'border-color': border,
        'border-width': 1.5,
        color: label,
        label: 'data(label)',
        'font-size': 10,
        'font-weight': 'normal',
        'text-wrap': 'wrap',
        'text-max-width': '110px',
        'text-valign': 'center',
        'text-halign': 'center',
        'z-index': 10,
      },
    },
    {
      selector: 'node[?entity_name]',
      style: { 'z-index': 20 },
    },
    {
      selector: 'node[entity_type = "Exchange"]',
      style: {
        'z-index': 30,
        'border-width': 2,
        'border-color': isDark ? '#fbbf24' : '#d97706',
      },
    },
    {
      selector: 'node[entity_category = "scam"]',
      style: {
        shape: 'triangle',
        width: 44,
        height: 44,
        'background-color': isDark ? '#2d1212' : '#fef2f2',
        'border-color': '#ef4444',
        'border-width': 3,
        'z-index': 40,
      },
    },
    {
      selector: 'node[risk_score >= 0.7]',
      style: {
        'border-color': '#ef4444',
        'border-width': 2.5,
      },
    },
    {
      selector: 'node:selected',
      style: {
        'border-color': '#f59e0b',
        'border-width': 3,
      },
    },
    {
      selector: 'edge',
      style: {
        width: 2,
        'line-color': edge,
        'target-arrow-color': edge,
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        label: 'data(label)',
        'font-size': 8,
        color: isDark ? '#94a3b8' : '#64748b',
        'text-background-color': isDark ? '#0f172a' : '#ffffff',
        'text-background-opacity': 0.85,
        'text-background-padding': '2px',
      },
    },
    {
      selector: 'edge:selected',
      style: {
        'line-color': '#f59e0b',
        'target-arrow-color': '#f59e0b',
        width: 3,
      },
    },
  ];
}

function runBreadthfirst(cy: cytoscape.Core) {
  cy.layout({
    name: 'breadthfirst',
    directed: true,
    padding: 36,
    spacingFactor: 1.15,
    animate: false,
  }).run();
  cy.fit(undefined, 40);
}

export function LandingWorkbenchDemo({ isDark }: { isDark: boolean }) {
  const navigate = useNavigate();
  const cyRef = useRef<cytoscape.Core | null>(null);
  const demo = demoGraphs[DEMO_ID];
  const elements = useMemo(() => demo?.elements ?? [], [demo]);
  const stylesheet = useMemo(() => landingWorkbenchStylesheet(isDark), [isDark]);
  const [selected, setSelected] = useState<{
    id: string;
    label: string;
    entity?: string;
    risk?: number;
  } | null>(null);

  const onCy = useCallback((cy: cytoscape.Core) => {
    cyRef.current = cy;
    cy.minZoom(0.2);
    cy.maxZoom(2.5);
    runBreadthfirst(cy);

    cy.on('tap', 'node', (evt) => {
      const n = evt.target;
      const d = n.data();
      setSelected({
        id: String(n.id()),
        label: String(d.label || n.id()),
        entity: d.entity_name ? String(d.entity_name) : undefined,
        risk: typeof d.risk_score === 'number' ? d.risk_score : undefined,
      });
      cy.elements().unselect();
      n.select();
      n.connectedEdges().select();
    });
    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        setSelected(null);
        cy.elements().unselect();
      }
    });
  }, []);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    // Theme change: remount via key on parent; still re-fit if stylesheet hot-swaps.
    requestAnimationFrame(() => cy.fit(undefined, 40));
  }, [isDark]);

  const zoomBy = (factor: number) => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.zoom({ level: cy.zoom() * factor, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
  };

  const openFullDemo = () => {
    if (!demo) return;
    const loggedIn = hasActiveSession();
    if (!loggedIn) enterGuestDemo();
    navigate(
      workbenchHrefForDemo(
        {
          demo: DEMO_ID,
          chain: demo.chain || 'TRX',
          address: demo.address || '',
        },
        { guest: !loggedIn },
      ),
    );
  };

  if (!demo) return null;

  return (
    <section
      id="workbench-demo"
      data-testid="landing-workbench-demo"
      className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 scroll-mt-8"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3 text-left">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">
            Graph Workbench demo
          </p>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
            Trace USDT through WazirX
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            {demo.description}
          </p>
        </div>
        <button
          type="button"
          onClick={openFullDemo}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-brand-gold-hover transition-colors"
          data-testid="landing-open-workbench-demo"
        >
          Open in Workbench
          <ExternalLink className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>

      <div className="rounded-2xl border border-border/80 bg-card shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border/70 bg-card/50">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Network className="w-3.5 h-3.5 text-primary" />
            <span>TRX · WazirX hot wallet</span>
            <span className="hidden sm:inline text-border">·</span>
            <span className="hidden sm:inline font-medium normal-case tracking-normal">
              Mock training graph
            </span>
          </div>
          <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[50%]">
            {demo.address}
          </span>
        </div>

        <div className="relative h-[min(560px,72vh)] bg-canvas">
          <CanvasGridBackground />
          <CytoscapeComponent
            key={isDark ? 'dark' : 'light'}
            elements={elements as any}
            stylesheet={stylesheet as any}
            style={{ width: '100%', height: '100%', position: 'relative', zIndex: 1 }}
            cy={onCy}
          />

          <div className="absolute bottom-3 left-3 z-[2] flex flex-col gap-1">
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => zoomBy(1.25)}
              className="w-8 h-8 rounded-md border border-border bg-card/95 shadow-xs flex items-center justify-center hover:bg-muted"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => zoomBy(0.8)}
              className="w-8 h-8 rounded-md border border-border bg-card/95 shadow-xs flex items-center justify-center hover:bg-muted"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              aria-label="Fit view"
              onClick={() => cyRef.current && cyRef.current.fit(undefined, 40)}
              className="w-8 h-8 rounded-md border border-border bg-card/95 shadow-xs flex items-center justify-center hover:bg-muted"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <p className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[2] text-[10px] text-muted-foreground bg-card/80 border border-border/50 rounded-md px-3 py-1 pointer-events-none hidden sm:block">
            Pan · zoom · click a node — then open full Workbench for Inspector &amp; analysis
          </p>
        </div>

        {selected && (
          <div className="px-3 py-2.5 border-t border-border/70 bg-muted/20 text-left flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-xs font-semibold text-foreground">{selected.label}</span>
            {selected.entity && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-medium">
                {selected.entity}
              </span>
            )}
            {selected.risk !== undefined && (
              <span className="text-[10px] text-muted-foreground">
                risk {Math.round(selected.risk * 100)}%
              </span>
            )}
            <span className="text-[10px] font-mono text-muted-foreground ml-auto truncate max-w-[40%]">
              {selected.id}
            </span>
          </div>
        )}
      </div>

      <p className="mt-2 text-[11px] text-muted-foreground text-center">
        Illustrative Workbench mock — not live chain data. Open in Workbench for Transfers, Intelligence, and Analysis.
      </p>
    </section>
  );
}
